import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';

import FormHelpers from '../../../shared/forms/formHelpers';
import FormControl from '../../../shared/forms/formControl.component';
import Select from '../../../shared/reactSelect/select.component';
import Input from '../../../shared/forms/input/input.component';
import { OptionsPropType } from '../../../shared/propTypes/common';

import { customReportSetPropTypes, customReportsPropTypes, customReportDataPropTypes } from '../../../shared/customReports/propTypes';
import { getOptionsForReportSet, getReportsIncludedInReportSet, cachedGetUniqueQueryParams, getParamsForEachReport } from '../../../shared/customReports/helpers';

import getInitialState from './customReportSetEditor.initialState';
import TestCustomReportSetForm from './testCustomReportSetForm.component';
import CustomReportModal from '../customReportModal.component';
import ControlButtons from '../controlButtons.component';

class CustomReportSetEditor extends PureComponent {
  static propTypes = {
    setCustomReportQueryModified: PropTypes.func.isRequired,
    closeCustomReportPreview: PropTypes.func.isRequired,
    customReportData: customReportDataPropTypes,
    disableReadOnlyMode: PropTypes.func.isRequired,
    hideCustomReportForm: PropTypes.func.isRequired,
    fetchCustomReportParams: PropTypes.func.isRequired,
    showCustomReportQueryForm: PropTypes.func.isRequired,
    saveCustomReport: PropTypes.func.isRequired,
    testCustomReportSet: PropTypes.func.isRequired,
    showExportCustomReportInsertQueryModal: PropTypes.func.isRequired,

    customReports: customReportsPropTypes,

    customReportSetToEdit: customReportSetPropTypes,

    openForEdit: PropTypes.bool.isRequired,
    readOnly: PropTypes.bool.isRequired,
    canModify: PropTypes.bool.isRequired,
    isFetchingCustomReportParams: PropTypes.bool.isRequired,
    isSavingCustomReportSetInProgress: PropTypes.bool.isRequired,
    isTestCustomReportSetInProgress: PropTypes.bool.isRequired,

    customReportParamsOptions: PropTypes.objectOf(OptionsPropType),
  };

  constructor(props) {
    super(props);

    this.state = {
      ...getInitialState(props.customReportSetToEdit, props.customReports),
    };
  }

  changeProperty = event => {
    FormHelpers.changeProperty({
      event,
      setState: this.setState.bind(this),
      callback: () => {
        this.props.setCustomReportQueryModified(true);
      },
    });
  };

  changeReports = reports => {
    const reportsIds = reports.map(({ value }) => value);
    const reportsIncludedInReportSet = getReportsIncludedInReportSet(this.props.customReports, reportsIds);
    const {
      queryParams,
      paramToUniqueIdMapping,
      uniqueParams,
    } = cachedGetUniqueQueryParams(reportsIncludedInReportSet);

    this.setState(state => ({
      ...state,
      formData: {
        ...state.formData,
        reports: {
          value: reportsIds,
        },
      },
      queryParams,
      mapping: paramToUniqueIdMapping,
      uniqueParams,
      reportsIncludedInReportSet,
    }), this.validateAll);
  };

  updateQueryParams = queryParams =>
    this.setState(state => ({
      queryParams: {
        ...state.queryParams,
        ...queryParams,
      },
    }));

  saveCustomReportSet = () => {
    const {
      saveCustomReport,
      setCustomReportQueryModified,
      showCustomReportQueryForm,
      openForEdit,
    } = this.props;

    const saveStrategy = data => saveCustomReport({
      data,
      isEditMode: openForEdit,
    }, () => {
      showCustomReportQueryForm(data.id);
      if (setCustomReportQueryModified) {
        setCustomReportQueryModified(false);
      }
    });

    this.save(saveStrategy);
  };

  showInsertQueryModal = () => {
    const { showExportCustomReportInsertQueryModal, customReportSetToEdit } = this.props;
    showExportCustomReportInsertQueryModal({
      reportSetId: customReportSetToEdit.reportSetId,
    });
  };

  save = saveStrategy => {
    const { formData, isFormValid, queryParams } = this.state;

    if (!isFormValid) {
      this.setState({
        triedToSubmitForm: true,
      });

      return;
    }

    const data = {
      id: formData.id.value,
      customFlag: formData.display.value,
      name: formData.name.value,
      reports: formData.reports.value,
      customReportType: formData.customReportType.value,
      queryParams,
    };

    saveStrategy(data);
  };

  validate = (key, { isValid }) => {
    FormHelpers.validate({
      key,
      isValid,
      setState: this.setState.bind(this),
      callback: () => {
        this.setState({
          shouldResetValues: false,
        }, () => {
          this.validateAll();
        });
      },
    });
  };

  validationSubscription = (key, operation) => {
    FormHelpers.validationSubscription({
      key,
      operation,
      setState: this.setState.bind(this),
      callback: this.validateAll,
    });
  };

  validateAll = () => FormHelpers.validateAll(this.setState.bind(this));

  testCustomReportSet = () => {
    const { formData, queryParams, reportsIncludedInReportSet, mapping } = this.state;

    const queries = getParamsForEachReport({
      reports: reportsIncludedInReportSet,
      queryParams,
      mapping,
      generateFileName: name => name,
    });

    this.props.testCustomReportSet({
      queries,
      customReportType: formData.customReportType.value,
      name: formData.name.value,
      params: queryParams,
    });
  };

  render() {
    const {
      formData,
      triedToSubmitForm,
      shouldResetValues,
      queryParams,
      uniqueParams,
    } = this.state;

    const {
      customReports,
      customReportData,
      closeCustomReportPreview,
      readOnly,
      canModify,
      openForEdit,
      disableReadOnlyMode,
      hideCustomReportForm,
      fetchCustomReportParams,
      customReportParamsOptions,
      isFetchingCustomReportParams,
      isSavingCustomReportSetInProgress,
      customReportSetToEdit,
      isTestCustomReportSetInProgress,
    } = this.props;

    const customReportsForReportSetOptions = getOptionsForReportSet(customReports);

    const isLoadingDataToEdit = !!openForEdit && !customReportSetToEdit;

    const areValuesValid = formData.name.isValid && !!formData.reports.isValid;

    return (
      <div className="custom-report-editor-form page-content-container">
        <form>
          <div className="row">
            <div className="col-sm-12 col-lg-6">
              <FormControl
                onChange={this.changeProperty}
                name="name"
                label="Report Set Name"
                validate={this.validate}
                validationSubscription={this.validationSubscription}
                value={formData.name.value}
                required
                showErrorByForce={triedToSubmitForm}
                shouldReset={shouldResetValues}
                horizontal
              >
                <Input
                  disabled={readOnly}
                  type="text"
                />
              </FormControl>

              <FormControl
                onChange={this.changeReports}
                name="reports"
                label="Reports"
                validate={this.validate}
                validationSubscription={this.validationSubscription}
                value={formData.reports.value}
                required
                showErrorByForce={triedToSubmitForm}
                shouldReset={shouldResetValues}
                horizontal
              >
                <Select
                  clearable
                  multi
                  removeSelected
                  closeOnSelect={false}
                  options={customReportsForReportSetOptions}
                  disabled={readOnly}
                  virtualized
                />
              </FormControl>
            </div>
            <TestCustomReportSetForm
              queryParams={queryParams}
              uniqueParams={uniqueParams}
              readOnly={readOnly}
              updateQueryParams={this.updateQueryParams}
              fetchCustomReportParams={fetchCustomReportParams}
              customReportParamsOptions={customReportParamsOptions}
              isFetchingCustomReportParams={isFetchingCustomReportParams}
            />
          </div>
        </form>
        <ControlButtons
          readOnly={readOnly}
          canModify={canModify}
          isTestInProgress={isTestCustomReportSetInProgress}
          isSavingInProgress={isSavingCustomReportSetInProgress}
          isLoadingDataToEdit={isLoadingDataToEdit}
          areValuesValid={areValuesValid}
          onTestClick={this.testCustomReportSet}
          onCancelClick={hideCustomReportForm}
          onEditClick={disableReadOnlyMode}
          onSaveClick={this.saveCustomReportSet}
          onShowInsertQueryModal={this.showInsertQueryModal}
        />

        <CustomReportModal
          customReportData={customReportData}
          formData={formData}
          queryParams={queryParams}
          hideModal={closeCustomReportPreview}
          isVisible={Boolean(customReportData)}
        />
      </div>
    );
  }
}

export default CustomReportSetEditor;
